/**
 * ROWIE — PDP Shop the Look
 *
 * Ported from shoppable-image-editorial.js. Differences:
 * - No hotspots. A single CTA opens the drawer at card 1.
 * - Drawer enters from the bottom on mobile, from the right on desktop
 *   (handled entirely in CSS; the JS only toggles `is-open`).
 *
 * Retained from the source: card deck positioning, arrow navigation,
 * looping, swipe (fires only when the touch starts outside the active
 * card, so the tile's own carousel keeps its gestures), and the
 * scroll rewind on close.
 */

(function () {
  const SELECTORS = {
    root: '[data-rowie-stl]',
    cta: '[data-stl-open]',
    drawer: '[data-stl-drawer]',
    overlay: '[data-stl-overlay]',
    close: '[data-stl-close]',
    deck: '[data-stl-deck]',
    card: '.rowie-stl__card',
    prevArrow: '.rowie-stl__arrow--prev',
    nextArrow: '.rowie-stl__arrow--next',
    current: '[data-stl-current]',
  };

  const DESKTOP_MIN = 990;

  class PdpShopTheLook {
    constructor(root) {
      this.root = root;
      this.cta = root.querySelector(SELECTORS.cta);
      this.drawer = root.querySelector(SELECTORS.drawer);
      this.overlay = root.querySelector(SELECTORS.overlay);
      this.closeBtn = root.querySelector(SELECTORS.close);
      this.deck = root.querySelector(SELECTORS.deck);
      this.cards = [...root.querySelectorAll(SELECTORS.card)];
      this.prevArrow = root.querySelector(SELECTORS.prevArrow);
      this.nextArrow = root.querySelector(SELECTORS.nextArrow);
      this.currentEl = root.querySelector(SELECTORS.current);

      this.currentIndex = 0;

      if (!this.deck || this.cards.length === 0) return;

      this.init();
    }

    init() {
      this.moveDrawerToBody();
      this.moveCtaToGallery();
      this.bindOpenClose();
      this.bindArrows();
      this.bindSwipe();
      this.bindEscape();
      this.updateDeck();
    }


    // -----------------------------------------------------------------------
    // The overlay and drawer are position:fixed, but an ancestor of the
    // snippet establishes a stacking context, which traps them below the
    // gallery. Reparent to body so they sit in the root context.
    // -----------------------------------------------------------------------

    moveDrawerToBody() {
      if (this.overlay && this.overlay.parentElement !== document.body) {
        document.body.appendChild(this.overlay);
      }
      if (this.drawer && this.drawer.parentElement !== document.body) {
        document.body.appendChild(this.drawer);
      }
    }


    // -----------------------------------------------------------------------
    // The snippet renders inside the product content column, but the CTA
    // needs to sit over the gallery. Move it there on init.
    // -----------------------------------------------------------------------
    
    moveCtaToGallery() {
      if (!this.cta) return;
          const gallery = document.querySelector('.product-media-stage');
      if (gallery && !gallery.contains(this.cta)) {
        gallery.appendChild(this.cta);
      }
    }


    // -----------------------------------------------------------------------
    // Deck positioning
    // -----------------------------------------------------------------------

    updateDeck() {
      const total = this.cards.length;

      this.cards.forEach((card, i) => {
        const diff = i - this.currentIndex;

        if (diff === 0) {
          card.dataset.position = 'active';
        } else if (diff === 1 || (this.currentIndex === total - 1 && i === 0 && total > 2)) {
          card.dataset.position = 'next';
        } else if (diff === -1 || (this.currentIndex === 0 && i === total - 1 && total > 2)) {
          card.dataset.position = 'prev';
        } else {
          card.dataset.position = 'hidden';
        }
      });

      if (this.currentEl) {
        this.currentEl.textContent = this.currentIndex + 1;
      }
    }

    goTo(index) {
      if (index < 0 || index >= this.cards.length) return;
      this.currentIndex = index;
      this.updateDeck();
    }

    goNext() {
      this.goTo((this.currentIndex + 1) % this.cards.length);
    }

    goPrev() {
      this.goTo((this.currentIndex - 1 + this.cards.length) % this.cards.length);
    }

    // -----------------------------------------------------------------------
    // Open / close
    // -----------------------------------------------------------------------

    bindOpenClose() {
      if (this.cta) {
        this.cta.addEventListener('click', () => {
          if (this.root.classList.contains('is-open')) this.close();
          else this.open();
        });
      }
      if (this.closeBtn) {
        this.closeBtn.addEventListener('click', () => this.close());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }
    }

    open() {
     this.root.classList.add('is-open');
      document.body.classList.add('rowie-stl-open');
      if (this.cta) this.cta.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.rewindCardScroll();
      this.root.classList.remove('is-open');
      document.body.classList.remove('rowie-stl-open');
      if (this.cta) this.cta.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        if (!this.root.classList.contains('is-open')) {
          document.body.style.overflow = '';
        }
      }, 400);
    }

    bindEscape() {
      document.addEventListener('keyup', (e) => {
        if (e.key === 'Escape' && this.root.classList.contains('is-open')) {
          this.close();
        }
      });
    }

    // -----------------------------------------------------------------------
    // Arrows
    // -----------------------------------------------------------------------

    bindArrows() {
      if (this.prevArrow) {
        this.prevArrow.addEventListener('click', () => this.goPrev());
      }
      if (this.nextArrow) {
        this.nextArrow.addEventListener('click', () => this.goNext());
      }
    }

    // -----------------------------------------------------------------------
    // Swipe — only when the touch starts outside the active card, so the
    // tile's own image carousel keeps its own gestures.
    // -----------------------------------------------------------------------

    bindSwipe() {
      if (!this.deck) return;

      let startX = 0;
      let startY = 0;
      let isSwiping = false;
      let touchedActiveCard = false;

      this.deck.addEventListener('touchstart', (e) => {
        const activeCard = this.deck.querySelector('[data-position="active"]');
        touchedActiveCard = activeCard && activeCard.contains(e.target);

        if (touchedActiveCard) return;

        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isSwiping = false;
      }, { passive: true });

      this.deck.addEventListener('touchmove', (e) => {
        if (touchedActiveCard || !startX) return;

        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);

        if (dx > dy && dx > 10) isSwiping = true;
      }, { passive: true });

      this.deck.addEventListener('touchend', (e) => {
        if (touchedActiveCard || !isSwiping) {
          startX = 0;
          startY = 0;
          isSwiping = false;
          touchedActiveCard = false;
          return;
        }

        const dx = e.changedTouches[0].clientX - startX;

        if (Math.abs(dx) > 40) {
          if (dx < 0) this.goNext();
          else this.goPrev();
        }

        startX = 0;
        startY = 0;
        isSwiping = false;
        touchedActiveCard = false;
      }, { passive: true });
    }

    // -----------------------------------------------------------------------
    // The tile owns image ordering — slide 0 is already the ghost.
    // Just rewind scroll position on close.
    // -----------------------------------------------------------------------

    rewindCardScroll() {
      this.cards.forEach((card) => {
        const track = card.querySelector('.collection-card-carousel-track');
        if (!track) return;
        track.scrollLeft = 0;
        if (track.parentElement) track.parentElement.scrollLeft = 0;
      });
    }
  }

  function initAll() {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      if (root.dataset.initialised) return;
      root.dataset.initialised = 'true';
      new PdpShopTheLook(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
      root.removeAttribute('data-initialised');
    });
    initAll();
  });
})();
